/**
 * PROTOTYPE – texts not approved
 *
 * Geschichtenschritt round 3 — approved click-mockup layout
 * (`scratch/geschichtenschritt-entwurf.dc.html`, Sebastian 2026-10-08
 * evening). Round-2 flow preserved; the visual layer is new.
 *
 * One decision per step. Zurück preserves input.
 *
 *   step 1  path                → storyKind
 *     erlebt            → step 2 erlebt-field (textarea + Beispiele)
 *     erfunden          → step 2 erfunden-tile  (Eigene Idee tile + 2x4 grid)
 *                         → step 3 erfunden-field (prefilled textarea
 *                                                   + Beispiele)
 *
 * BUG FIX round 3: switching path on step 1 while the field still
 * holds the UNEDITED generated tile sentence clears the field; user-
 * edited text stays.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import {
  type ErfundenTileSlug,
  type StoryKind,
  useBookConfiguratorStore,
} from '../../state/book-configurator-store';
import { CREATE_BOOK_ORDER_DESCRIPTION_MAX_LENGTH } from '../../constants/text-limits';
import { CharacterCounter } from '../geschichte/CharacterCounter';
import { SchrittBlock, type SchrittBlockState } from '../personen/SchrittBlock';
import {
  adventureQuestionForCast,
  erfundenFieldHeading,
  erfundenSentenceForTile,
} from './erfunden-prefill';
import { ChevronIcon, MicrophoneIcon, TilePictogram } from './tile-pictograms';

// ──────────────────────────────────────────────────────────────────
// Brand palette (Dreamable kit)
// ──────────────────────────────────────────────────────────────────

const NAVY = '#0A2342';
const GOLD = '#FFC94D';
const SOFT_BEIGE = '#FFF4DE';
const LIGHT_BLUE = '#F2F5FA';
const WHITE = '#FFFFFF';
const STEEL = '#445771';
const SKY_LIGHT = '#E3EFFF';
const TILE_BORDER = '#E1E6EF';
const TILE_ON_BG = '#FFF7E6';
const BEISPIELE_SEP = '#E3D3B0';
const EIGENE_BORDER = '#EBD9B0';

// ──────────────────────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────────────────────

export interface GeschichtenschrittBlockProps {
  readonly state: SchrittBlockState;
  readonly freitext: string;
  readonly setFreitext: (text: string) => void;
  readonly onWeiter: () => void;
  readonly onEdit: () => void;
  readonly cast: readonly { key: string; name: string }[];
  /**
   * Analytics (feat/story-step-golive, Sebastian ruling 2026-10-09).
   * Optional so the component stays usable without analytics in
   * isolated test / dev contexts. PersonenRoute wires these to the
   * consent-guarded `fireBookAnalytics` helper (sessionStorage dedup
   * by key); the component itself never calls PostHog directly.
   */
  readonly onStoryKindChosen?: (kind: StoryKind) => void;
  readonly onErfundenTileChosen?: (tile: ErfundenTileSlug) => void;
}

type Screen = 'path' | 'erlebt-field' | 'erfunden-tile' | 'erfunden-field';

const ERFUNDEN_GRID_ORDER: readonly ErfundenTileSlug[] = [
  'feuerwehr',
  'weltraum',
  'piraten',
  'superhelden',
  'polizei',
  'zauberwald',
  'dino',
  'unterwasser',
];

function initialScreen(
  storyKind: StoryKind | null,
  erfundenTile: ErfundenTileSlug | null,
): Screen {
  if (storyKind === null) return 'path';
  if (storyKind === 'erlebt') return 'erlebt-field';
  return erfundenTile === null ? 'erfunden-tile' : 'erfunden-field';
}

// ──────────────────────────────────────────────────────────────────
// Examples (shared Beispiele model)
// ──────────────────────────────────────────────────────────────────

interface Example {
  readonly key: string;
  readonly label: string;
  readonly first: string;
  readonly full: string;
}

function firstSentence(text: string): string {
  // Mockup rule: split on '. ' and take first + '.'. Preserves
  // whichever punctuation already exists at the end of the first
  // sentence; falls back to the whole text when no mid-sentence
  // period is present.
  const idx = text.indexOf('. ');
  return idx === -1 ? text : text.slice(0, idx + 1);
}

function erlebtExamples(): readonly Example[] {
  const bt = STRINGS.book.personen.beispieltexte;
  const an = STRINGS.book.personen.anlaesse;
  return [
    { key: 'urlaub', label: an.urlaub, first: firstSentence(bt.urlaub), full: bt.urlaub },
    { key: 'einschulung', label: an.einschulung, first: firstSentence(bt.einschulung), full: bt.einschulung },
    { key: 'geburtstag', label: an.geburtstag, first: firstSentence(bt.geburtstag), full: bt.geburtstag },
    { key: 'ersterKitaTag', label: an.ersterKitaTag, first: firstSentence(bt.ersterKitaTag), full: bt.ersterKitaTag },
  ];
}

/**
 * Round 4 (Sebastian 2026-10-09 10:15): the erfunden Beispiele box
 * narrows to a single matching example when a theme tile is selected,
 * and shows the full 8-example list only on 'eigene'. Astronaut (Mia)
 * is removed from the geschichtenschritt example list entirely — the
 * production string `STRINGS.book.personen.invented.astronaut` stays
 * because `InspirationRow` on the Personen step still references it.
 *
 * Mapping (tile → example key):
 *   feuerwehr   → inventedPrototype.feuerwehr     (Emil)
 *   weltraum    → inventedPrototype.weltraum      (Oskar, NEW)
 *   piraten     → inventedPrototype.piraten       (Frieda)
 *   superhelden → invented.superheld              (Lena, production)
 *   polizei     → invented.polizist               (Til,  production)
 *   zauberwald  → inventedPrototype.zauberwald    (Noah)
 *   dino        → inventedPrototype.dinosaurier   (Leni)
 *   unterwasser → inventedPrototype.unterwasser   (Jakob, NEW)
 *
 * 'eigene' order: Superheld, Polizist, Weltraum, Feuerwehr, Piraten,
 * Zauberwald, Dinosaurier, Unterwasser.
 */
function makeExample(
  key: string,
  src: { readonly label: string; readonly ausfuehrlich: string },
): Example {
  return {
    key,
    label: src.label,
    first: firstSentence(src.ausfuehrlich),
    full: src.ausfuehrlich,
  };
}

function erfundenExamplesForTile(tile: ErfundenTileSlug): readonly Example[] {
  const prod = STRINGS.book.personen.invented;
  const proto = STRINGS.book.geschichtenschrittPrototype.inventedPrototype;
  const SINGLE: Readonly<Record<Exclude<ErfundenTileSlug, 'eigene'>, Example>> = {
    feuerwehr: makeExample('feuerwehr', proto.feuerwehr),
    weltraum: makeExample('weltraum', proto.weltraum),
    piraten: makeExample('piraten', proto.piraten),
    superhelden: makeExample('superheld', prod.superheld),
    polizei: makeExample('polizist', prod.polizist),
    zauberwald: makeExample('zauberwald', proto.zauberwald),
    dino: makeExample('dinosaurier', proto.dinosaurier),
    unterwasser: makeExample('unterwasser', proto.unterwasser),
  };
  if (tile === 'eigene') {
    return [
      makeExample('superheld', prod.superheld),
      makeExample('polizist', prod.polizist),
      makeExample('weltraum', proto.weltraum),
      makeExample('feuerwehr', proto.feuerwehr),
      makeExample('piraten', proto.piraten),
      makeExample('zauberwald', proto.zauberwald),
      makeExample('dinosaurier', proto.dinosaurier),
      makeExample('unterwasser', proto.unterwasser),
    ];
  }
  return [SINGLE[tile]];
}

function erfundenExamplesHeading(tile: ErfundenTileSlug): string {
  const s = STRINGS.book.geschichtenschrittPrototype;
  if (tile === 'eigene') return s.examplesHeading;
  return s.examplesHeadingThemed.replace('{Thema}', s.erfundenThemes[tile]);
}

// ──────────────────────────────────────────────────────────────────
// Container
// ──────────────────────────────────────────────────────────────────

export function GeschichtenschrittBlock(props: GeschichtenschrittBlockProps) {
  const s = STRINGS.book.geschichtenschrittPrototype;

  const storyKind = useBookConfiguratorStore((x) => x.storyKind);
  const erfundenTile = useBookConfiguratorStore((x) => x.erfundenTile);
  const lastGeneratedText = useBookConfiguratorStore((x) => x.lastGeneratedText);
  const textErfunden = useBookConfiguratorStore((x) => x.textErfunden);
  const setStoryKind = useBookConfiguratorStore((x) => x.setStoryKind);
  const setErfundenTile = useBookConfiguratorStore((x) => x.setErfundenTile);
  const setLastGeneratedText = useBookConfiguratorStore((x) => x.setLastGeneratedText);
  const setTextErfunden = useBookConfiguratorStore((x) => x.setTextErfunden);

  const [screen, setScreen] = useState<Screen>(() => initialScreen(storyKind, erfundenTile));

  /**
   * Round 3b: insert a freshly generated sentence into the field AND
   * record it as the "last generated" string. Future comparisons check
   * `textErfunden === lastGeneratedText` verbatim — the stored string,
   * not a recomputed one — so a cast change doesn't make an unedited
   * sentence look user-edited.
   *
   * Round 3c: the setter is `setFreitext` which routes to the current
   * kind's bucket; by the time this fires we are always on the
   * erfunden flow (handleErfundenTileChoice is the only caller), so
   * the write lands in textErfunden by construction.
   */
  const applyGeneratedText = useCallback(
    (text: string) => {
      props.setFreitext(text);
      setLastGeneratedText(text.length === 0 ? null : text);
    },
    [props, setLastGeneratedText],
  );

  /**
   * Round 3c: switching path on step 1 just flips storyKind; the
   * store's setStoryKind hydrates `freitext` from the OTHER kind's
   * bucket, so the field shows whatever that side remembers (empty if
   * untouched). The old "clear unedited generated sentence on kind
   * switch" rule from round 3b is retired by Sebastian — textErfunden
   * KEEPS the generated sentence; it just stops being visible under
   * erlebt and reappears on the way back.
   *
   * `erfundenTile` is likewise preserved across kind switches so the
   * resume on a return to erfunden lands directly on the field screen
   * (not back on the tile picker).
   */
  const { onStoryKindChosen, onErfundenTileChosen } = props;

  const handlePathChoice = useCallback(
    (choice: StoryKind) => {
      setStoryKind(choice);
      onStoryKindChosen?.(choice);
      // Round-3c ruling (Sebastian 2026-10-08 23:40): on an in-session
      // kind flip we ALWAYS land on the top of the chosen kind's
      // flow — erlebt-field or the erfunden-tile overview. A user
      // who returns has doubted the previous choice and should see
      // the overview again (one decision per step). The previously
      // picked tile stays `aria-pressed`/highlighted on the tile
      // screen, so no information is lost.
      //
      // Reload-resume (mount-time) is a different code path:
      // `initialScreen(storyKind, erfundenTile)` runs ONCE inside
      // the `useState` initialiser and lands on the field directly
      // when a tile is set. That path is intentionally NOT shared
      // with this handler, so the two can evolve independently.
      setScreen(choice === 'erlebt' ? 'erlebt-field' : 'erfunden-tile');
    },
    [setStoryKind, onStoryKindChosen],
  );

  const handleErfundenTileChoice = useCallback(
    (nextTile: ErfundenTileSlug) => {
      const nextPrefill = erfundenSentenceForTile(nextTile, props.cast);
      if (erfundenTile === null) {
        setErfundenTile(nextTile);
        applyGeneratedText(nextPrefill);
        onErfundenTileChosen?.(nextTile);
        setScreen('erfunden-field');
        return;
      }
      if (nextTile === erfundenTile) {
        // Same-tile re-tap: no new decision, no analytics fire — the
        // consumer-side dedup keyed on tile would absorb it anyway,
        // but short-circuiting here keeps the analytics-emission call
        // sites 1:1 with "customer picked a new tile for the first
        // time this session".
        setScreen('erfunden-field');
        return;
      }
      // Round-3b/3c: compare the erfunden-bucket text against the
      // LAST STORED generated string. The eigene → theme switch has a
      // null anchor; we treat an empty bucket as "unedited" there.
      const unedited =
        (lastGeneratedText !== null && textErfunden === lastGeneratedText) ||
        (lastGeneratedText === null && textErfunden.length === 0);
      if (unedited) {
        setErfundenTile(nextTile);
        applyGeneratedText(nextPrefill);
        onErfundenTileChosen?.(nextTile);
        setScreen('erfunden-field');
        return;
      }
      // eslint-disable-next-line no-alert
      const ok = window.confirm(s.tileReplaceConfirmLabel);
      if (ok) {
        setErfundenTile(nextTile);
        applyGeneratedText(nextPrefill);
        onErfundenTileChosen?.(nextTile);
        setScreen('erfunden-field');
      }
    },
    [
      applyGeneratedText,
      erfundenTile,
      lastGeneratedText,
      props.cast,
      s.tileReplaceConfirmLabel,
      setErfundenTile,
      textErfunden,
      onErfundenTileChosen,
    ],
  );

  /**
   * Round 3b (3c-refined): if the cast changes while an erfunden
   * theme tile is selected and the erfunden-bucket text still
   * matches the stored generated string, regenerate with the new
   * names and update both `textErfunden` and `lastGeneratedText`.
   * An edited bucket is left untouched. The `eigene` tile has no
   * sentence to regenerate, so it is skipped.
   *
   * Round-3c key change: the check runs REGARDLESS of the current
   * `storyKind`. When the user is on erlebt, the erfunden bucket
   * refreshes in the background — a return to erfunden shows the
   * up-to-date sentence with the current cast. `setTextErfunden`
   * only touches `freitext` when the current kind is erfunden, so
   * the erlebt view is never disturbed.
   */
  useEffect(() => {
    if (erfundenTile === null || erfundenTile === 'eigene') return;
    if (lastGeneratedText === null) return;
    if (textErfunden !== lastGeneratedText) return;
    const nextSentence = erfundenSentenceForTile(erfundenTile, props.cast);
    if (nextSentence === lastGeneratedText) return;
    setTextErfunden(nextSentence);
    setLastGeneratedText(nextSentence);
  }, [props.cast, erfundenTile, lastGeneratedText, textErfunden, setLastGeneratedText, setTextErfunden]);

  const summary = useMemo(() => {
    const trimmed = props.freitext.trim();
    if (trimmed.length === 0) return s.summaryEmpty;
    return `${trimmed.slice(0, 60)}${trimmed.length > 60 ? '…' : ''}`;
  }, [props.freitext, s.summaryEmpty]);

  return (
    <SchrittBlock
      n={2}
      title={STRINGS.book.personen.freitextTitle}
      state={props.state}
      summary={summary}
      onEdit={props.onEdit}
    >
      <div className="gs-root" style={{ fontFamily: "'Vend Sans', system-ui, sans-serif", color: NAVY }}>
        {screen === 'path' && (
          <ScreenPath selected={storyKind} onChoose={handlePathChoice} />
        )}
        {screen === 'erlebt-field' && (
          <ScreenErlebtField
            freitext={props.freitext}
            setFreitext={props.setFreitext}
            onBack={() => setScreen('path')}
            onWeiter={props.onWeiter}
          />
        )}
        {screen === 'erfunden-tile' && (
          <ScreenErfundenTile
            selected={erfundenTile}
            cast={props.cast}
            onPick={handleErfundenTileChoice}
            onBack={() => setScreen('path')}
          />
        )}
        {screen === 'erfunden-field' && erfundenTile !== null && (
          <ScreenErfundenField
            tile={erfundenTile}
            freitext={props.freitext}
            setFreitext={props.setFreitext}
            onBack={() => setScreen('erfunden-tile')}
            onWeiter={props.onWeiter}
          />
        )}
      </div>
    </SchrittBlock>
  );
}

// ──────────────────────────────────────────────────────────────────
// Step 1 — path
// ──────────────────────────────────────────────────────────────────

function ScreenPath({
  selected,
  onChoose,
}: {
  readonly selected: StoryKind | null;
  readonly onChoose: (p: StoryKind) => void;
}) {
  const s = STRINGS.book.geschichtenschrittPrototype;
  // Round-3b FIX 1: inner heading removed — the outer SchrittBlock
  // already renders "2. Worum soll es in deinem Buch gehen?" above
  // this body, so a second h1 was duplicate chrome.
  // Round-3c FIX 1: equal image heights across the two cards. The
  // source images have different aspect ratios (700×440 vs 630×480),
  // so natural-ratio rendering made the cards differ in height.
  // Both images now render at a fixed 4:3 ratio via `aspect-ratio`
  // + `object-fit: cover`. `object-position: center top` prefers
  // the top of the source — the two source images frame their
  // subjects (family / astronaut) near the top-centre, so cover-
  // cropping with centre-top preserves heads at both 360px and
  // 1280px widths. HTML width/height attrs are a 4:3 pair (800×600)
  // to reserve the correct aspect-ratio before the image loads.
  // The two card buttons stretch to equal height via the flex row's
  // default align-items, and the text block hugs the bottom via
  // `marginTop: 'auto'` so the two text blocks line up.
  const choiceBase: React.CSSProperties = {
    flex: '1 1 0',
    minWidth: 0,
    border: '3px solid transparent',
    borderRadius: 20,
    padding: '8px 8px 14px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: 'inherit',
  };
  const erlebtStyle: React.CSSProperties = {
    ...choiceBase,
    background: SOFT_BEIGE,
    borderColor: selected === 'erlebt' ? NAVY : 'transparent',
  };
  const erfundenStyle: React.CSSProperties = {
    ...choiceBase,
    background: SKY_LIGHT,
    borderColor: selected === 'erfunden' ? NAVY : 'transparent',
  };
  const imgStyle: React.CSSProperties = {
    width: '100%',
    aspectRatio: '4 / 3',
    objectFit: 'cover',
    objectPosition: 'center top',
    borderRadius: 14,
    display: 'block',
  };
  const textStackStyle: React.CSSProperties = {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  };
  const titleStyle: React.CSSProperties = {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.25,
    padding: '0 6px',
  };
  const linkStyle: React.CSSProperties = {
    fontSize: 13,
    lineHeight: 1.4,
    color: STEEL,
    padding: '0 6px',
  };
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      data-testid="geschichte-screen-path"
    >
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: STEEL }}>
        {s.pathChoiceSubline}
      </p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
        <button
          type="button"
          onClick={() => onChoose('erlebt')}
          aria-pressed={selected === 'erlebt'}
          data-testid="path-erlebt"
          style={erlebtStyle}
        >
          <img
            src="/geschichte/geschichte-erlebt.jpg"
            width={800}
            height={600}
            alt={s.pathChoiceErlebtImgAlt}
            style={imgStyle}
          />
          <span style={textStackStyle}>
            <span style={titleStyle}>{s.pathChoiceErlebtTitle}</span>
            <span style={linkStyle}>{s.pathChoiceErlebtExamples}</span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => onChoose('erfunden')}
          aria-pressed={selected === 'erfunden'}
          data-testid="path-erfunden"
          style={erfundenStyle}
        >
          <img
            src="/geschichte/geschichte-erfunden.jpg"
            width={800}
            height={600}
            alt={s.pathChoiceErfundenImgAlt}
            style={imgStyle}
          />
          <span style={textStackStyle}>
            <span style={titleStyle}>{s.pathChoiceErfundenTitle}</span>
            <span style={linkStyle}>{s.pathChoiceErfundenExamples}</span>
          </span>
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Step 2 erfunden — tiles
// ──────────────────────────────────────────────────────────────────

function ScreenErfundenTile({
  selected,
  cast,
  onPick,
  onBack,
}: {
  readonly selected: ErfundenTileSlug | null;
  readonly cast: readonly { key: string; name: string }[];
  readonly onPick: (t: ErfundenTileSlug) => void;
  readonly onBack: () => void;
}) {
  const s = STRINGS.book.geschichtenschrittPrototype;
  const question = adventureQuestionForCast(cast);
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
      data-testid="geschichte-screen-erfunden-tile"
    >
      <h1 style={{ margin: 0, fontSize: 24, lineHeight: 1.25, fontWeight: 700 }}>
        {question}
      </h1>
      <EigeneIdeeTile selected={selected === 'eigene'} onPick={() => onPick('eigene')} />
      <p
        style={{
          margin: '4px 0 -6px',
          fontSize: 14,
          fontWeight: 600,
          color: STEEL,
        }}
      >
        {s.orSearchAdventureLead}
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 10,
        }}
      >
        {ERFUNDEN_GRID_ORDER.map((slug) => (
          <ErfundenTile
            key={slug}
            slug={slug}
            label={s.erfundenTiles[slug]}
            selected={selected === slug}
            onPick={() => onPick(slug)}
          />
        ))}
      </div>
      <div>
        <BackButton onBack={onBack} />
      </div>
    </div>
  );
}

function EigeneIdeeTile({
  selected,
  onPick,
}: {
  readonly selected: boolean;
  readonly onPick: () => void;
}) {
  const s = STRINGS.book.geschichtenschrittPrototype;
  const style: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 14,
    minHeight: 72,
    padding: '12px 16px',
    borderRadius: 16,
    border: `2px solid ${selected ? NAVY : EIGENE_BORDER}`,
    background: SOFT_BEIGE,
    textAlign: 'left',
    fontSize: 15,
    fontWeight: 700,
    color: NAVY,
    cursor: 'pointer',
    width: '100%',
    fontFamily: 'inherit',
  };
  const iconBox: React.CSSProperties = {
    width: 48,
    height: 48,
    borderRadius: 14,
    background: WHITE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 48px',
  };
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      data-testid="tile-erfunden-eigene"
      style={style}
    >
      <span style={iconBox}>
        <TilePictogram slug="eigene" />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span>{s.eigeneIdeeTitle}</span>
        <span style={{ fontSize: 13, fontWeight: 500, color: STEEL }}>
          {s.eigeneIdeeSubline}
        </span>
      </span>
    </button>
  );
}

function ErfundenTile({
  slug,
  label,
  selected,
  onPick,
}: {
  readonly slug: ErfundenTileSlug;
  readonly label: string;
  readonly selected: boolean;
  readonly onPick: () => void;
}) {
  const style: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 8px 12px',
    borderRadius: 16,
    border: `2px solid ${selected ? NAVY : TILE_BORDER}`,
    background: selected ? TILE_ON_BG : WHITE,
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 700,
    minHeight: 112,
    color: NAVY,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
  const iconBox: React.CSSProperties = {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: LIGHT_BLUE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      data-testid={`tile-erfunden-${slug}`}
      style={style}
    >
      <span style={iconBox}>
        <TilePictogram slug={slug} />
      </span>
      <span>{label}</span>
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────
// Field screens (shared layout)
// ──────────────────────────────────────────────────────────────────

function ScreenErlebtField({
  freitext,
  setFreitext,
  onBack,
  onWeiter,
}: {
  readonly freitext: string;
  readonly setFreitext: (s: string) => void;
  readonly onBack: () => void;
  readonly onWeiter: () => void;
}) {
  const s = STRINGS.book.geschichtenschrittPrototype;
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      data-testid="geschichte-screen-erlebt-field"
    >
      <h1 style={{ margin: 0, fontSize: 24, lineHeight: 1.25, fontWeight: 700 }}>
        {s.erlebtFieldHeading}
      </h1>
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: STEEL }}>
        {STRINGS.book.personen.freitextSubtitle}
      </p>
      <FreitextArea
        value={freitext}
        onChange={setFreitext}
        placeholder={s.erlebtPlaceholder}
        testId="erlebt-textarea"
      />
      <CharacterCounter
        value={freitext.length}
        corridor={{ min: 0, max: CREATE_BOOK_ORDER_DESCRIPTION_MAX_LENGTH }}
      />
      <DictateHint />
      <NavRow
        onBack={onBack}
        onWeiter={onWeiter}
        weiterDisabled={freitext.trim().length === 0}
        weiterTestId="erlebt-weiter"
      />
      <BeispieleBox
        heading={STRINGS.book.geschichtenschrittPrototype.examplesHeading}
        examples={erlebtExamples()}
        testIdPrefix="example-erlebt"
      />
    </div>
  );
}

function ScreenErfundenField({
  tile,
  freitext,
  setFreitext,
  onBack,
  onWeiter,
}: {
  readonly tile: ErfundenTileSlug;
  readonly freitext: string;
  readonly setFreitext: (s: string) => void;
  readonly onBack: () => void;
  readonly onWeiter: () => void;
}) {
  const s = STRINGS.book.geschichtenschrittPrototype;
  const heading = erfundenFieldHeading(tile);
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      data-testid="geschichte-screen-erfunden-field"
    >
      <h1 style={{ margin: 0, fontSize: 24, lineHeight: 1.25, fontWeight: 700 }}>
        {heading}
      </h1>
      <FreitextArea
        value={freitext}
        onChange={setFreitext}
        placeholder={s.erfundenPlaceholder}
        testId="erfunden-textarea"
      />
      <CharacterCounter
        value={freitext.length}
        corridor={{ min: 0, max: CREATE_BOOK_ORDER_DESCRIPTION_MAX_LENGTH }}
      />
      <DictateHint />
      <p style={{ margin: 0, fontSize: 14, color: STEEL }}>{s.erfundenFieldMoreHint}</p>
      <NavRow
        onBack={onBack}
        onWeiter={onWeiter}
        weiterDisabled={freitext.trim().length === 0}
        weiterTestId="erfunden-weiter"
      />
      <BeispieleBox
        heading={erfundenExamplesHeading(tile)}
        examples={erfundenExamplesForTile(tile)}
        testIdPrefix="example-erfunden"
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Field primitives
// ──────────────────────────────────────────────────────────────────

function FreitextArea({
  value,
  onChange,
  placeholder,
  testId,
}: {
  readonly value: string;
  readonly onChange: (s: string) => void;
  readonly placeholder: string;
  readonly testId: string;
}) {
  const style: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 150,
    border: `2px solid ${NAVY}`,
    borderRadius: 14,
    padding: 14,
    fontFamily: 'inherit',
    fontSize: 16,
    lineHeight: 1.5,
    color: NAVY,
    resize: 'vertical',
    background: WHITE,
  };
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={STRINGS.book.personen.freitextTitle}
      maxLength={CREATE_BOOK_ORDER_DESCRIPTION_MAX_LENGTH}
      data-testid={testId}
      style={style}
    />
  );
}

function DictateHint() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        color: STEEL,
      }}
    >
      <MicrophoneIcon />
      <span>{STRINGS.book.personen.freitextDictateHint}</span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Nav row — Zurück / Weiter
// ──────────────────────────────────────────────────────────────────

function BackButton({ onBack }: { readonly onBack: () => void }) {
  const s = STRINGS.book.geschichtenschrittPrototype;
  const style: React.CSSProperties = {
    border: `2px solid ${NAVY}`,
    borderRadius: 12,
    padding: '14px 22px',
    fontSize: 16,
    fontWeight: 700,
    minHeight: 48,
    background: WHITE,
    color: NAVY,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
  return (
    <button type="button" onClick={onBack} style={style} data-testid="nav-back">
      {s.zurueckLabel}
    </button>
  );
}

function NavRow({
  onBack,
  onWeiter,
  weiterDisabled,
  weiterTestId,
}: {
  readonly onBack: () => void;
  readonly onWeiter: () => void;
  readonly weiterDisabled?: boolean;
  readonly weiterTestId: string;
}) {
  const s = STRINGS.book.geschichtenschrittPrototype;
  const weiterStyle: React.CSSProperties = {
    border: 'none',
    borderRadius: 12,
    padding: '14px 22px',
    fontSize: 16,
    fontWeight: 700,
    minHeight: 48,
    background: GOLD,
    color: NAVY,
    cursor: 'pointer',
    opacity: weiterDisabled ? 0.6 : 1,
    fontFamily: 'inherit',
  };
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        marginTop: 4,
      }}
    >
      <BackButton onBack={onBack} />
      <button
        type="button"
        onClick={onWeiter}
        disabled={weiterDisabled}
        data-testid={weiterTestId}
        style={weiterStyle}
      >
        {s.weiterLabel}
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Beispiele box — accordion, soft-beige background
// ──────────────────────────────────────────────────────────────────

function BeispieleBox({
  heading,
  examples,
  testIdPrefix,
}: {
  readonly heading: string;
  readonly examples: readonly Example[];
  readonly testIdPrefix: string;
}) {
  const s = STRINGS.book.geschichtenschrittPrototype;
  const [openKey, setOpenKey] = useState<string | null>(null);
  return (
    <section
      style={{
        marginTop: 14,
        background: SOFT_BEIGE,
        borderRadius: 18,
        padding: '16px 16px 6px',
      }}
      data-testid={`${testIdPrefix}-list`}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 6,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: NAVY }}>
          {heading}
        </h2>
        <span style={{ fontSize: 13, color: STEEL }}>{s.examplesTapHint}</span>
      </div>
      {examples.map((ex) => {
        const isOpen = openKey === ex.key;
        const rowStyle: React.CSSProperties = {
          width: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '12px 4px',
          background: 'none',
          border: 0,
          borderTop: `1px solid ${BEISPIELE_SEP}`,
          textAlign: 'left',
          cursor: 'pointer',
          color: NAVY,
          fontFamily: 'inherit',
        };
        return (
          <div key={ex.key}>
            <button
              type="button"
              onClick={() => setOpenKey(isOpen ? null : ex.key)}
              aria-expanded={isOpen}
              data-testid={`${testIdPrefix}-${ex.key}-toggle`}
              style={rowStyle}
            >
              <ChevronIcon open={isOpen} />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{ex.label}</span>
                <span style={{ fontSize: 13, lineHeight: 1.4, color: STEEL }}>
                  {ex.first}
                </span>
              </span>
            </button>
            {isOpen && (
              <p
                style={{
                  margin: '0 0 12px 28px',
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: NAVY,
                  whiteSpace: 'pre-wrap',
                }}
                data-testid={`${testIdPrefix}-${ex.key}-full`}
              >
                {ex.full}
              </p>
            )}
          </div>
        );
      })}
    </section>
  );
}
